import React, { useCallback, useState, useEffect, useMemo, useRef } from "react"
import { ActivityIndicator, FlatList, StyleSheet } from "~/ui/primitives"
import styled, { useTheme } from "~/ui/styled"
import { useSafeAreaInsets } from "~/ui/safe-area"
// import { observer } from "mobx-react-lite"
import {
  StartFlex,
  ColumnFlex,
  CenterFlex,
  Text,
  BetweenFlex,
  IconFont,
  // Button
} from "~components/El"
import { SearchBar } from "~components/SearchBar"
import { ChatTypes } from "~proto"
import { useStores } from "~models"
import { Avatar } from "~widgets/avatar"
import useNavigation from "~hooks/useNavigation"
// import { useModal } from "~hooks/useModal"
// import { usePopup } from "~hooks/usePopup"
import { globalSpinner, formatMessageTime } from "~utils"

const Container = styled(ColumnFlex)`
  flex: 1;
  width: 100%;
  min-height: 0;
  margin-bottom: 30px;
`

const GroupMembersItem = React.memo(
  ({ item, index, onClick }: { item: any; index: number; onClick: (item: any) => void }) => {
  // console.log("item", item)
  const theme = useTheme()
  // const { Dialog } = useModal()
  // const popup = usePopup()
  return (
    <ColumnFlex width="100%" onClick={() => onClick(item)}>
      <BetweenFlex width="100%" padding="0 12px" bg={theme.colors.blockBackground}>
        <Avatar avatar={item.image} size={40} name={item.title} />
        <ColumnFlex flex={1} height="auto" ml="14px">
          <BetweenFlex width="100%" padding="10px 0">
            <ColumnFlex flex={3} mr="10px">
              <Text color={theme.colors.text} fontSize="14px" fontWeight="600">
                {item.title || ""}
              </Text>
              {item.desc && (
                <Text numberOfLines={2} color={theme.colors.secondary} fontSize="12px" mt="4px">
                  {item.desc}
                </Text>
              )}
            </ColumnFlex>
            <ColumnFlex flex={1} style={{ alignItems: "flex-end" }}>
              <Text color={theme.colors.secondaryText} fontSize="12px">
                {formatMessageTime(item.created)}
              </Text>
              <StartFlex width="auto" mt="10px">
                <StartFlex width="auto">
                  <IconFont name="qlltz" size={12} color={theme.colors.secondary} />
                  <Text color={theme.colors.secondary} fontSize="12px" ml="4px">
                    {item.member_count}
                  </Text>
                </StartFlex>
              </StartFlex>
            </ColumnFlex>
          </BetweenFlex>
          <CenterFlex height={StyleSheet.hairlineWidth} width="100%" bg={theme.colors.border} />
        </ColumnFlex>
      </BetweenFlex>
    </ColumnFlex>
  )
})

const GroupList: React.FC<any> = ({route}) => {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const navigation = useNavigation()
  const { apiStore, chatStore } = useStores()
  const [searchText, setSearchText] = useState("")
  const [list, setList] = useState<any[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const requestGuardRef = useRef(0)

  const LIMIT = 20

  const mergeUnique = useCallback((prev: any[], next: any[]) => {
    const map = new Map<string, any>()
    for (const item of prev || []) {
      const key = (item?.id ?? item?.chat_id ?? "")?.toString?.() || ""
      if (key) map.set(key, item)
    }
    for (const item of next || []) {
      const key = (item?.id ?? item?.chat_id ?? "")?.toString?.() || ""
      if (key) map.set(key, item)
    }
    return Array.from(map.values())
  }, [])

  const getList = useCallback(async (nextPage = 1) => {
    try {
      const reqId = Date.now()
      requestGuardRef.current = reqId

      if (nextPage === 1) {
        globalSpinner.show()
      } else {
        setLoadingMore(true)
      }
      const result = await apiStore.getJoinChatGroupAndChannelList({
        type: ChatTypes.GROUP,
        page: nextPage,
        limit: LIMIT,
      })
      // 若请求被后续请求覆盖，则丢弃旧结果
      if (requestGuardRef.current !== reqId) return

      const next = result?.data || []
      setList((prev) => (nextPage === 1 ? next : mergeUnique(prev, next)))
      setPage(nextPage)
      // 返回条数不足 limit 时认为无更多
      setHasMore(Array.isArray(next) ? next.length >= LIMIT : false)
    } catch (error) {
      // ignore
    } finally {
      if (nextPage === 1) {
        globalSpinner.close()
      } else {
        setLoadingMore(false)
      }
    }
  }, [LIMIT, apiStore, mergeUnique])

  useEffect(() => {
    void getList(1)
  }, [getList])

  const filteredList = useMemo(() => {
    if (!searchText.trim()) return list

    const keyword = searchText.toLowerCase()

    return list.filter(item =>
      item.title?.toLowerCase().includes(keyword) ||
      item.desc?.toLowerCase().includes(keyword)
    )
  }, [list, searchText])

  const keyExtractor = useCallback((item, idx) => item.id || idx.toString(), [])

  const handlePressGroup = useCallback(
    (item) => {
      const cid = String(item?.chat_id ?? item?.name ?? "").trim()
      const numericId = item?.id != null ? Number(item.id) : null
      if (!cid && numericId == null) return

      let chat =
        chatStore.getChatByMessageRef?.({
          chat_cid: cid || null,
          chat_id: numericId,
        }) ??
        (cid ? chatStore.byChatId?.(cid) || chatStore.chats?.get?.(cid) : null) ??
        (numericId != null ? chatStore.byId?.(numericId) : null)

      if (!chat) {
        // 本地没有会话时先创建占位，会在后续事件/消息到来时被补全
        chat = chatStore.newGroupChat?.({
          chat_id: cid || String(numericId),
          id: numericId ?? undefined,
          name: cid || undefined,
          type: ChatTypes.GROUP,
          title: item?.title || "群聊",
          desc: item?.desc || null,
          image: item?.image || "",
          members: [],
          created: item?.created || Date.now(),
          updated: item?.updated || item?.created || Date.now(),
        } as any)
      }

      if (!chat?.chat_id) return

      // 从群列表进入时，若该会话之前被本地隐藏，则恢复它（同时清除本机 deleted 记录）
      if (chat.deleted) {
        chatStore.reviveChat?.(chat)
      } else {
        chat.setDeleted?.(false)
      }
      navigation.navigate("Chat", { chat_id: chat.chat_id })
    },
    [chatStore, navigation],
  )

  const renderItem = useCallback(({ item, index }) => (
    <GroupMembersItem item={item} index={index} onClick={handlePressGroup} />
  ),[handlePressGroup])

  const handleEndReached = useCallback(() => {
    if (!hasMore) return
    if (loadingMore) return
    void getList(page + 1)
  }, [getList, hasMore, loadingMore, page])

  const Footer = useMemo(() => {
    if (!list.length) return null
    if (loadingMore) {
      return (
        <CenterFlex padding="12px 0">
          <ActivityIndicator />
          <Text ml="8px" color={theme.colors.secondaryText} fontSize="12px">
            加载中…
          </Text>
        </CenterFlex>
      )
    }
    if (!hasMore) {
      return (
        <CenterFlex padding="12px 0">
          <Text color={theme.colors.secondaryText} fontSize="12px">
            没有更多了
          </Text>
        </CenterFlex>
      )
    }
    return null
  }, [hasMore, list.length, loadingMore, theme.colors.secondaryText])

  return (
    <Container>
        <ColumnFlex width="100%" height="auto" padding="10px" bg={theme.colors.blockBackground}>
            <SearchBar
                value={searchText}
                placeholder="搜索群聊"
                style={{
                    backgroundColor: theme.colors.background,
                }}
                onChange={setSearchText}
                onClear={() => setSearchText("")}
            />
        </ColumnFlex>
        <ColumnFlex
            width="100%"
            style={ { flex: 1, minHeight: 0 }}
        > 
            <FlatList
                  style={{ flex: 1, minHeight: 0, width: "100%" }}
                  data={filteredList}
                  keyExtractor={keyExtractor}
                  contentContainerStyle={{ flexGrow: 1, width: "100%", paddingBottom: insets.bottom + 60 }}
                  renderItem={renderItem}
                  onEndReached={handleEndReached}
                  onEndReachedThreshold={0.2}
                  ListFooterComponent={Footer}
                  removeClippedSubviews
                />
        </ColumnFlex>
      </Container>
  )
}

export default GroupList
